/*
 * Continuous looping product media gallery — mobile only.
 *
 * Dawn's mobile gallery is a native scroll-snap strip, which dead-ends on the
 * last image. This swaps it for an Embla carousel with `loop: true`, the same
 * technique us.fable.com uses (they run Flickity's `wrapAround`): the strip is
 * transform-driven inside an overflow-hidden viewport, and slides that leave
 * one end are re-positioned at the other *while the finger is still moving*.
 * The last image therefore feeds straight into the first with no seam and no
 * post-swipe correction.
 *
 * Nothing is cloned, so every [data-media-id] stays unique and Dawn's own
 * lightbox, thumbnail and variant logic keep working untouched.
 *
 * Desktop is left completely alone: the carousel is only built while the mobile
 * media query matches, and is torn back down to Dawn's markup above it.
 */
(function () {
  var MOBILE = '(max-width: 749px)';
  var mql = window.matchMedia(MOBILE);
  // Embla ships as a separate theme asset next to this file.
  var ownSrc = document.currentScript ? document.currentScript.src : '';

  function LoopGallery(sliderComponent) {
    this.component = sliderComponent;
    this.list = sliderComponent.querySelector('[id^="Slider-Gallery-"]');
    this.counterCurrent = sliderComponent.querySelector('.slider-counter--current');
    this.counterTotal = sliderComponent.querySelector('.slider-counter--total');
    this.prevButton = sliderComponent.querySelector('button[name="previous"]');
    this.nextButton = sliderComponent.querySelector('button[name="next"]');
    this.embla = null;

    if (!this.list) return;

    this.sync = this.sync.bind(this);
    this.onSelect = this.onSelect.bind(this);
    this.onPrev = this.onPrev.bind(this);
    this.onNext = this.onNext.bind(this);

    this.sync();
    // Three ways across the breakpoint: the media query itself, a rotation, and
    // a plain resize — browsers are not uniform about firing all of them.
    mql.addEventListener('change', this.sync);
    window.addEventListener('orientationchange', this.sync);
    var timer = null;
    var self = this;
    window.addEventListener('resize', function () {
      clearTimeout(timer);
      timer = setTimeout(self.sync, 200);
    });
  }

  LoopGallery.prototype.sync = function () {
    if (mql.matches) this.build();
    else this.teardown();
  };

  LoopGallery.prototype.slides = function () {
    return Array.prototype.filter.call(this.list.children, function (el) {
      return el.classList.contains('slider__slide');
    });
  };

  LoopGallery.prototype.build = function () {
    if (this.embla || typeof EmblaCarousel === 'undefined') return;
    if (this.slides().length < 2) return;

    // Embla wants an overflow-hidden viewport whose first child is the strip.
    // Dawn's <ul> becomes the strip; we only add the viewport around it.
    this.viewport = document.createElement('div');
    this.viewport.className = 'gallery-loop__viewport';
    this.list.parentNode.insertBefore(this.viewport, this.list);
    this.viewport.appendChild(this.list);
    this.list.setAttribute('data-gallery-loop', '');

    this.embla = EmblaCarousel(this.viewport, {
      loop: true,
      align: 'center',
      skipSnaps: false,
      duration: 22
    });

    // Dawn's slider keeps deriving its state from scrollLeft, which is now
    // pinned at 0 — its ResizeObserver would reset the counter to "1" mid-swipe
    // and dispatch slideChanged with no element. Mute it while Embla drives.
    this.dawnUpdate = this.component.update;
    this.dawnInitPages = this.component.initPages;
    this.component.update = function () {};
    this.component.initPages = function () {};

    this.embla.on('select', this.onSelect);
    // Dawn's own button handlers scroll the (no longer scrolling) list, so stop
    // them in the capture phase rather than tearing the listeners off — that
    // way desktop gets its untouched buttons back on teardown.
    if (this.prevButton) this.prevButton.addEventListener('click', this.onPrev, true);
    if (this.nextButton) this.nextButton.addEventListener('click', this.onNext, true);

    this.onSelect();
  };

  LoopGallery.prototype.teardown = function () {
    if (!this.embla) return;
    if (this.prevButton) this.prevButton.removeEventListener('click', this.onPrev, true);
    if (this.nextButton) this.nextButton.removeEventListener('click', this.onNext, true);
    this.embla.destroy();
    this.embla = null;
    if (this.dawnUpdate) {
      this.component.update = this.dawnUpdate;
      this.component.initPages = this.dawnInitPages;
      this.dawnUpdate = null;
    }
    this.list.removeAttribute('data-gallery-loop');
    this.viewport.parentNode.insertBefore(this.list, this.viewport);
    this.viewport.remove();
    this.viewport = null;
  };

  LoopGallery.prototype.onPrev = function (event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    this.embla.scrollPrev();
  };

  LoopGallery.prototype.onNext = function (event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    this.embla.scrollNext();
  };

  LoopGallery.prototype.onSelect = function () {
    var index = this.embla.selectedScrollSnap();
    var slides = this.slides();

    if (this.counterCurrent) this.counterCurrent.textContent = index + 1;
    if (this.counterTotal) this.counterTotal.textContent = slides.length;
    // Looping means neither arrow is ever a dead end.
    if (this.prevButton) this.prevButton.removeAttribute('disabled');
    if (this.nextButton) this.nextButton.removeAttribute('disabled');

    slides.forEach(function (slide, i) {
      slide.classList.toggle('is-active', i === index);
    });

    if (window.pauseAllMedia) window.pauseAllMedia();
  };

  function setup(root) {
    (root || document).querySelectorAll('media-gallery [id^="GalleryViewer-"]').forEach(function (component) {
      if (component.loopGallery) return;
      component.loopGallery = new LoopGallery(component);
    });
  }

  function watch() {
    setup(document);
    // The gallery is re-rendered on variant change; re-run when that happens.
    document.querySelectorAll('.product__media-wrapper').forEach(function (wrapper) {
      if (wrapper.dataset.loopWatched) return;
      wrapper.dataset.loopWatched = 'true';
      new MutationObserver(function () {
        setup(wrapper);
      }).observe(wrapper, { childList: true, subtree: true });
    });
  }

  function withEmbla(callback) {
    if (window.EmblaCarousel) return callback();
    var src = ownSrc.replace(/infinite-gallery\.js(\?.*)?$/, 'embla-carousel.js$1');
    if (src === ownSrc) return; // not where we expected it; fail quietly
    var script = document.createElement('script');
    script.src = src;
    script.onload = callback;
    document.head.appendChild(script);
  }

  function start() {
    withEmbla(watch);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
  document.addEventListener('shopify:section:load', start);
})();
